const BASE = '/api/signup';

export interface PublicRate {
  unitAmountDecimal: string;
  currency: string;
}

export interface PublicPlan {
  id: string;
  name: string;
  unitAmount: number;
  currency: string;
  interval: string;
  includedTextSegments: number;
  includedAiCredits: number;
  description: string | null;
  brandedSubdomain: boolean;
  /** Free trial length in days; 0 means no trial (charged immediately). */
  trialDays: number;
  overage: { text: PublicRate | null; ai: PublicRate | null };
  topUpPacks: {
    id: string;
    name: string;
    meter: string;
    quantity: number;
    unitAmount: number;
    currency: string;
  }[];
}

export async function listPlans(): Promise<PublicPlan[]> {
  const r = await fetch(`${BASE}/plans`);
  if (!r.ok) throw new Error('failed to load plans');
  return (await r.json()) as PublicPlan[];
}

export interface CreateSignupBody {
  communityName: string;
  adminName: string;
  adminEmail: string;
  planId: string;
  turnstileToken: string;
  /** Optional desired subdomain slug. Sent only for plans entitled to a
   * branded subdomain; the API re-validates and 409s on a race collision. */
  slug?: string;
}

export type SlugCheck = { slug: string; available: boolean; reason: 'invalid' | 'taken' | null };

/**
 * Advisory availability check for a desired subdomain slug. Callers treat
 * failures as non-blocking (the API re-validates on submit).
 */
export async function checkSlug(slug: string): Promise<SlugCheck> {
  const r = await fetch(`${BASE}/slug-check?slug=${encodeURIComponent(slug)}`);
  if (!r.ok) throw new Error('slug check failed');
  return (await r.json()) as SlugCheck;
}

export async function createSignup(
  body: CreateSignupBody,
): Promise<{ signupId: string; clientSecret: string; intentType: 'setup' | 'payment' }> {
  const r = await fetch(BASE, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const msg = await r.json().catch(() => ({}));
    throw new Error((msg as { message?: string }).message ?? 'signup failed');
  }
  return (await r.json()) as {
    signupId: string;
    clientSecret: string;
    intentType: 'setup' | 'payment';
  };
}

export async function getStatus(id: string): Promise<{ status: string }> {
  const r = await fetch(`${BASE}/${id}/status`);
  if (!r.ok) throw new Error('status check failed');
  return (await r.json()) as { status: string };
}
